import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";

import {
  siteSessionAccessTokenKeyName,
  siteSessionIdTokenKeyName,
  siteSessionRefresh,
  siteSessionTokenKeyName,
} from "@/config/environment";
import { API_BASE_URL } from "@/config/network";
import { useBackendStatus } from "@/hooks/useBackendStatus";
import { resetSessionAndRedirectToSignIn } from "@/modules/auth/session";
import * as authServices from "@/services/auth";
import { isBackendUnavailableError } from "@/tools/httpErrors";

import { getCookie, setCookie } from "./cookies";

// Extend AxiosRequestConfig to include retry count
interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  __retryCount?: number;
}

export const interceptor = () => {
  axios.interceptors.request.use(
    (config) => {
      const siteSession = getCookie(siteSessionTokenKeyName);

      config.headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: siteSession ? `Bearer ${siteSession}` : undefined,
        ...config.headers,
      } as AxiosRequestHeaders;
      config.baseURL ??= API_BASE_URL;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      if (isBackendUnavailableError(error)) {
        useBackendStatus.getState().markBackendUnavailable();
      }

      if (error.response?.status === 401) {
        const config = error.config as RetryableAxiosRequestConfig;
        const retryCount = config?.__retryCount || 0;
        const maxRetries = 1;

        if (retryCount >= maxRetries) {
          resetSessionAndRedirectToSignIn();
          return Promise.reject(error);
        }

        const refreshToken = getCookie(siteSessionRefresh);

        if (!refreshToken) {
          resetSessionAndRedirectToSignIn();
          return Promise.reject(error);
        }

        try {
          const response = await authServices.refreshToken(refreshToken);

          if (!response?.idToken) {
            throw new Error("Invalid refresh token response");
          }

          const {
            accessToken,
            idToken,
            refreshToken: nextRefreshToken,
          } = response;

          setCookie(siteSessionTokenKeyName, idToken);
          setCookie(siteSessionAccessTokenKeyName, accessToken);
          if (nextRefreshToken) {
            setCookie(siteSessionRefresh, nextRefreshToken);
          }
          if (idToken) {
            setCookie(siteSessionIdTokenKeyName, idToken);
          }

          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${idToken}`,
          };

          config.__retryCount = retryCount + 1;

          return axios.request(config);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          resetSessionAndRedirectToSignIn();
        }
      }

      return Promise.reject(error);
    },
  );
};
